import { Element as SlateElement } from 'slate';
import type {
    BulletedListBlock,
    CalloutBlock,
    CodeBlock,
    EmbedBlock,
    HorizontalLineBlock,
    ImageBlock,
    ListItemBlock,
    NumberedListBlock,
    ParagraphBlock,
    SlateBlock,
    SlateLeaf,
    TableBlock,
    TableCellBlock,
    TableRowBlock,
    VideoEmbedBlock,
} from '@atlas/renderer';
import { TOP_LEVEL_BLOCK_TYPES } from '@atlas/tool/box/slate/isTopLevelBlock';
import isNotFalsy from '@atlas/tool/box/isNotFalsy';
import type { TextBlock } from '@atlas/tool/box/slate/isTextBlock';
import type { VideoBlock, VideoBlockSource } from '@atlas/renderer/video-block';
import isMultiLineTextBlock, { type MultiLineTextBlock } from '@atlas/tool/box/slate/isMultiLineTextBlock';
import isOneLineTextBlock, {
    ONE_LINE_TEXT_BLOCK_TYPES,
    type OneLineTextBlock,
} from '@atlas/tool/box/slate/isOneLineTextBlock';
import { LIST_BLOCKS_TYPES } from '@atlas/tool/box/slate/isListBlock';

export default function normalizeEditorChildren(children?: unknown): SlateBlock[] {
    if (!children) return [{ children: [{ text: '' }], type: 'paragraph' }];
    if (typeof children === 'string') return [{ children: [{ text: children }], type: 'paragraph' }];
    if (SlateElement.isElement(children)) {
        const block = extractBlockWithChildren(
            children,
            isOneLineTextBlock(children) || isMultiLineTextBlock(children) ? children.type : 'paragraph',
        );
        if (!block) return [{ children: [{ text: '' }], type: 'paragraph' }];
        return [block];
    }
    if (!Array.isArray(children)) return [{ children: [{ text: '' }], type: 'paragraph' }];

    const newChildren = children
        .map<SlateBlock | SlateBlock[] | null>((topChild: unknown) => {
            if (!topChild) return null;

            if (typeof topChild !== 'object' || !('type' in topChild) || typeof topChild.type !== 'string') {
                return extractTextBlockFromWhatever(topChild);
            }

            const topBlock = topChild as { type: string };

            if (!allBlockTypes.includes(topBlock.type)) return extractBlockWithChildren(topBlock);

            if (topBlock.type === 'horizontal-line') {
                return {
                    children: [{ text: '' }],
                    type: 'horizontal-line',
                } as HorizontalLineBlock;
            }

            if (topBlock.type === 'image') {
                if (!('src' in topBlock) || !topBlock.src || typeof topBlock.src !== 'string') return null;

                return {
                    ...extractObjectProps(topBlock, {
                        alt: ['string'],
                        link: ['string'],
                        width: ['number'],
                        fingerprint: ['string'],
                        originalWidth: ['number'],
                        originalHeight: ['number'],
                        fileName: ['string'],
                        handle: ['string'],
                    }),
                    src: topBlock.src,
                    children: [{ text: '' }],
                    type: 'image',
                } as ImageBlock;
            }

            if (topBlock.type === 'video') {
                if (
                    !('sources' in topBlock) ||
                    !topBlock.sources ||
                    !Array.isArray(topBlock.sources) ||
                    !topBlock.sources.length
                )
                    return null;

                const validSources = topBlock.sources.filter(
                    (source: unknown) =>
                        source &&
                        typeof source === 'object' &&
                        'type' in source &&
                        'url' in source &&
                        typeof source.type === 'string' &&
                        typeof source.url === 'string',
                );
                if (!validSources.length) return null;

                return {
                    ...extractObjectProps(topBlock, {
                        alt: ['string'],
                        width: ['number'],
                        height: ['number'],
                    }),
                    sources: validSources as [VideoBlockSource, ...VideoBlockSource[]],
                    children: [{ text: '' }],
                    type: 'video',
                } as VideoBlock;
            }

            if (topBlock.type === 'video-embed') {
                if (!('resourceId' in topBlock) || !topBlock.resourceId || typeof topBlock.resourceId !== 'string')
                    return null;
                if (!('service' in topBlock) || !topBlock.service || typeof topBlock.service !== 'string') return null;
                if (!['youtube', 'vimeo', 'loom'].includes(topBlock.service)) return null;

                return {
                    ...extractObjectProps(topBlock, {
                        alt: ['string'],
                    }),
                    service: topBlock.service as VideoEmbedBlock['service'],
                    resourceId: topBlock.resourceId,
                    children: [{ text: '' }],
                    type: 'video-embed',
                } as VideoEmbedBlock;
            }

            if (topBlock.type === 'embed') {
                if (!('source' in topBlock) || !topBlock.source || typeof topBlock.source !== 'string') return null;

                return {
                    ...extractObjectProps(topBlock, {
                        alt: ['string'],
                        height: ['number'],
                    }),
                    source: topBlock.source,
                    children: [{ text: '' }],
                    type: 'embed',
                } as EmbedBlock;
            }

            if (topBlock.type === 'paragraph' || topBlock.type === 'block-quote') {
                // Paragraph should be multiline already
                return extractBlockWithChildren(topBlock, topBlock.type as MultiLineTextBlock['type']);
            }

            if (topBlock.type === 'code-block') {
                // Paragraph should be multiline already
                const block = extractBlockWithChildren(topBlock, topBlock.type as MultiLineTextBlock['type']);
                if (!block) return null;
                return {
                    ...extractObjectProps(topBlock, { language: ['string'] }),
                    children: block.children.map((child) =>
                        extractObjectProps(child, { text: ['string', true], link: ['string'] }),
                    ),
                    type: 'code-block',
                } as CodeBlock;
            }

            if (ONE_LINE_TEXT_BLOCK_TYPES.includes(topBlock.type as OneLineTextBlock['type'])) {
                const newBlock = extractBlockWithChildren(topBlock) as OneLineTextBlock;
                if (!newBlock) return { type: topBlock.type, children: [{ text: '' }] } as OneLineTextBlock;

                return {
                    children: newBlock.children.map((child) => ({
                        ...child,
                        text: child.text.replace(/\n+/g, ' '),
                    })),
                    type: topBlock.type,
                } as OneLineTextBlock;
            }

            if (topBlock.type === 'callout') {
                if (
                    !('children' in topBlock) ||
                    !Array.isArray(topBlock.children) ||
                    !topBlock.children.length ||
                    !Array.isArray(topBlock.children)
                )
                    return null;
                const iconBlock = topBlock.children.find(
                    (c: unknown) => c && typeof c === 'object' && 'type' in c && c.type === 'icon',
                );
                const paragraphBlocks = topBlock.children
                    .filter(
                        (c: unknown): c is { type: 'paragraph' } =>
                            !!c &&
                            typeof c === 'object' &&
                            (('type' in c && c.type === 'paragraph') || ('text' in c && typeof c.text === 'string')),
                    )
                    .map((c) => extractTextBlockFromWhatever(c))
                    .filter(isNotFalsy);

                return [
                    {
                        children: [
                            {
                                icon: iconBlock?.icon && typeof iconBlock.icon === 'string' ? iconBlock.icon : '💡',
                                children: [{ text: '' }],
                                type: 'icon',
                            },
                            paragraphBlocks[0] ?? {
                                children: [{ text: '' }],
                                type: 'paragraph',
                            },
                        ],
                        type: 'callout',
                    } as CalloutBlock,
                    ...paragraphBlocks.slice(1),
                ];
            }

            if (LIST_BLOCKS_TYPES.includes(topBlock.type as NumberedListBlock['type'] | BulletedListBlock['type'])) {
                return extractListBlock(topBlock as { type: NumberedListBlock['type'] | BulletedListBlock['type'] });
            }

            if (topBlock.type === 'table-cell') {
                const cellBlock = extractBlockWithChildren(topBlock, 'table-cell') as TableCellBlock | null;
                if (!cellBlock || !cellBlock.children.length) return null;
                if (cellBlock.children.length === 1 && !cellBlock.children[0].text) return null;

                return {
                    children: [
                        {
                            children: [cellBlock],
                            type: 'table-row',
                        },
                    ],
                    type: 'table',
                } as TableBlock;
            }

            if (topBlock.type === 'table-row') {
                const cells = extractTableCells(topBlock);
                if (!cells) return null;

                return {
                    children: [
                        {
                            children: cells,
                            type: 'table-row',
                        },
                    ],
                    type: 'table',
                } as TableBlock;
            }

            if (topBlock.type === 'table') {
                if (!('children' in topBlock) || !Array.isArray(topBlock.children)) {
                    return extractTextBlockFromWhatever(topBlock);
                }

                let maxCells = 0;
                const rows = topBlock.children
                    .map((c) => extractTableCells(c))
                    .map<TableRowBlock | null>((children) => {
                        if (!children?.length) return null;
                        maxCells = Math.max(maxCells, children.length);
                        return { children, type: 'table-row' };
                    })
                    .filter(isNotFalsy)
                    .map((row) => {
                        while (row.children.length < maxCells) {
                            row.children.push({ children: [{ text: '' }], type: 'table-cell' });
                        }
                        return row;
                    });

                if (!rows.length) return extractTextBlockFromWhatever(topBlock);

                const columns =
                    'columns' in topBlock && Array.isArray(topBlock.columns)
                        ? topBlock.columns.map(
                              (c) =>
                                  (c &&
                                      typeof c === 'object' &&
                                      typeof c.width === 'number' &&
                                      c.width > 0 && { width: c.width }) ||
                                  {},
                          )
                        : undefined;

                return { ...(columns && { columns }), children: rows, type: 'table' } as TableBlock;
            }

            return extractTextBlockFromWhatever(topBlock);
        })
        .filter(isNotFalsy)
        .flat();

    if (!newChildren.length) return [{ children: [{ text: '' }], type: 'paragraph' } as ParagraphBlock];
    return newChildren;
}

// #region Helpers

const allBlockTypes = [...TOP_LEVEL_BLOCK_TYPES, 'list-item', 'table-row', 'table-cell', 'icon'];

function extractObjectProps<T extends Record<string, unknown>>(
    block: T,
    propsTypes: {
        // keyof T
        [key: string]: [
            type: 'string' | 'number' | 'boolean',
            fallback?: string | number | boolean,
            required?: boolean,
        ];
    },
): Partial<T> {
    const props: Partial<T> = {};

    for (const [key, [type, fallback, required]] of Object.entries(propsTypes) as [
        keyof T,
        typeof propsTypes[keyof typeof propsTypes],
    ][]) {
        if (typeof block[key] === 'undefined') {
            if (!required) continue;
            if (typeof fallback !== 'undefined') {
                props[key] = fallback as typeof props[typeof key];
            } else {
                props[key] = (type === 'string' ? '' : type === 'number' ? 0 : false) as typeof props[typeof key];
            }
        } else if (typeof block[key] !== type) {
            if (typeof fallback !== 'undefined') {
                props[key] = fallback as typeof props[typeof key];
            }
        } else {
            props[key] = block[key];
        }
    }

    return props;
}

function extractBlockWithChildren(
    probablyBlock: unknown,
    blockType: TextBlock['type'] | ListItemBlock['type'] | TableCellBlock['type'] = 'paragraph',
): SlateBlock | null {
    if (!probablyBlock || typeof probablyBlock !== 'object') return null;
    if (!('children' in probablyBlock)) return null;
    const { children } = probablyBlock;
    if (typeof children === 'string') return { type: blockType, children: [{ text: children }] };
    if (!Array.isArray(children)) return null;
    const newChildren = children
        .map<SlateLeaf | null>((child) => {
            if (!child) return null;
            if (typeof child === 'string') return { text: child };
            if (typeof child !== 'object') return null;
            if (!('text' in child)) return null;
            if (typeof child.text !== 'string') return null;
            return {
                ...extractObjectProps(child, {
                    bold: ['boolean'],
                    italic: ['boolean'],
                    underline: ['boolean'],
                    strikethrough: ['boolean'],
                    code: ['boolean'],
                    link: ['string'],
                    constant: ['string'],
                }),
                ...(child.data && typeof child.data === 'object' && { data: child.data }), // TODO: Maybe add validation
                text: child.text,
            };
        })
        .filter(isNotFalsy);

    if (newChildren.length === 0) return null;

    return { type: blockType, children: newChildren };
}

function extractTextBlockFromWhatever(
    whatever: unknown,
    type: TextBlock['type'] | ListItemBlock['type'] | TableCellBlock['type'] = 'paragraph',
): SlateBlock | null {
    if (!whatever) return null;
    if (typeof whatever === 'string') return { children: [{ text: whatever }], type };
    if (typeof whatever !== 'object') return null;
    if (Array.isArray(whatever)) {
        return whatever.reduce((final, c) => {
            const block = extractTextBlockFromWhatever(c);
            if (!block) return final;
            if (!final) return block;
            return { ...final, children: [...final.children, block.children] };
        }, null);
    }
    if (SlateElement.isElement(whatever)) {
        return extractBlockWithChildren(whatever, type);
    }
    if ('children' in whatever) {
        if (typeof whatever.children === 'string') {
            return { children: [{ text: whatever.children }], type };
        }
        if (Array.isArray(whatever.children)) {
            return extractBlockWithChildren(whatever, type);
        }
    }

    if ('text' in whatever && whatever.text && typeof whatever.text === 'string') {
        return extractBlockWithChildren({ children: [whatever.text], type }, type);
    }
    return null;
}

function extractTableCells(possiblyRow: unknown): TableCellBlock[] | null {
    if (!possiblyRow) return null;
    if (typeof possiblyRow !== 'object' || !('children' in possiblyRow)) return null;
    if (!Array.isArray(possiblyRow.children) || !possiblyRow.children.length) return null;

    if (!Array.isArray(possiblyRow.children) || !possiblyRow.children.length) return null;
    const cells = possiblyRow.children
        .map((c) => extractTextBlockFromWhatever(c, 'table-cell') as TableCellBlock | null)
        .filter(isNotFalsy);

    if (!cells.length) return null;
    return cells;
}

function extractListBlock(possiblyBlock: {
    type: NumberedListBlock['type'] | BulletedListBlock['type'];
}): NumberedListBlock | BulletedListBlock | null {
    if (!('children' in possiblyBlock) || !Array.isArray(possiblyBlock.children)) return null;
    if (!possiblyBlock.children.length) return null;

    const children = possiblyBlock.children
        .map<ListItemBlock | NumberedListBlock | BulletedListBlock | null>((child: unknown) => {
            if (child && typeof child === 'object' && 'type' in child) {
                if (child.type === 'numbered-list' || child.type === 'bulleted-list')
                    return extractListBlock(child as { type: NumberedListBlock['type'] | BulletedListBlock['type'] });
                return extractBlockWithChildren(child, 'list-item') as ListItemBlock | null;
            }
            return extractTextBlockFromWhatever(child, 'list-item') as ListItemBlock | null;
        })
        .filter(isNotFalsy);

    if (!children.length) return null;

    return {
        ...extractObjectProps(possiblyBlock, { start: ['number'] }),
        children,
        type: possiblyBlock.type,
    };
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('normalizeEditorChildren', async () => {
        it('returns empty paragraph if input is empty', () => {
            expect(normalizeEditorChildren()).toStrictEqual([{ children: [{ text: '' }], type: 'paragraph' }]);
            expect(normalizeEditorChildren(null)).toStrictEqual([{ children: [{ text: '' }], type: 'paragraph' }]);
            expect(normalizeEditorChildren([])).toStrictEqual([{ children: [{ text: '' }], type: 'paragraph' }]);
        });

        it('replaces strings with normal text objects', () => {
            expect(normalizeEditorChildren('string ok')).toStrictEqual([
                { children: [{ text: 'string ok' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren(['array of strings ok'])).toStrictEqual([
                { children: [{ text: 'array of strings ok' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ children: 'children string ok', type: 'paragraph' }])).toStrictEqual([
                { children: [{ text: 'children string ok' }], type: 'paragraph' },
            ]);
            expect(
                normalizeEditorChildren([{ children: ['array of string children ok'], type: 'paragraph' }]),
            ).toStrictEqual([{ children: [{ text: 'array of string children ok' }], type: 'paragraph' }]);
        });

        it('replaces incorrect blocks on the top level with paragraphs', () => {
            expect(normalizeEditorChildren([{ children: [{ text: 'content' }], type: 'UNKNOWN' }])).toStrictEqual([
                { children: [{ text: 'content' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ children: [{ text: 'content' }] }])).toStrictEqual([
                { children: [{ text: 'content' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ children: 'content' }])).toStrictEqual([
                { children: [{ text: 'content' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ text: 'content' }])).toStrictEqual([
                { children: [{ text: 'content' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ children: ['this', { text: 'is' }, 'content'] }])).toStrictEqual([
                { children: [{ text: 'this' }, { text: 'is' }, { text: 'content' }], type: 'paragraph' },
            ]);
        });

        it('restores hr', () => {
            expect(normalizeEditorChildren([{ type: 'horizontal-line' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'horizontal-line' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'horizontal-line', children: [{ text: 'content' }] }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'horizontal-line' }]);
        });

        it('restores image', () => {
            // Removes unrestorable image and adds empty paragraph
            expect(normalizeEditorChildren([{ type: 'image' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ type: 'image', src: 'src' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'image', src: 'src' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'image', src: 'src', children: [{ text: 'content' }] }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'image', src: 'src' }]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'image',
                        src: 'src',
                        alt: 0, // should be removed
                        link: 'link',
                        width: 2,
                        fingerprint: 'fingerprint',
                        originalWidth: 1,
                        originalHeight: 2,
                        fileName: 'fileName',
                        handle: 'handle',
                        children: [{ text: '' }],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [{ text: '' }],
                    type: 'image',
                    src: 'src',
                    link: 'link',
                    width: 2,
                    fingerprint: 'fingerprint',
                    originalWidth: 1,
                    originalHeight: 2,
                    fileName: 'fileName',
                    handle: 'handle',
                },
            ]);
        });

        it('restores video', () => {
            expect(normalizeEditorChildren([{ type: 'video' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'paragraph' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'video', sources: [{ type: 'source', url: 'url' }] }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'video', sources: [{ type: 'source', url: 'url' }] }]);
            expect(
                normalizeEditorChildren([
                    { type: 'video', sources: [{ type: 'source', url: 'url' }], children: 'loool' },
                ]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'video', sources: [{ type: 'source', url: 'url' }] }]);
            expect(normalizeEditorChildren([{ type: 'video', sources: ['none'] }])).toStrictEqual([
                { children: [{ text: '' }], type: 'paragraph' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'video', sources: [0, { type: 'source', url: 'url' }] }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'video', sources: [{ type: 'source', url: 'url' }] }]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'video',
                        alt: 'alt',
                        width: 100,
                        height: 200,
                        sources: [{ type: 'source', url: 'url' }],
                        children: [{ text: '' }],
                    },
                ]),
            ).toStrictEqual([
                {
                    type: 'video',
                    alt: 'alt',
                    width: 100,
                    height: 200,
                    sources: [{ type: 'source', url: 'url' }],
                    children: [{ text: '' }],
                },
            ]);
        });

        it('restores video-embed', () => {
            expect(normalizeEditorChildren([{ type: 'video-embed' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'paragraph' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'video-embed', resourceId: 'resourceId', service: 'service' }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'video-embed',
                        resourceId: 'resourceId',
                        service: 'youtube',
                    },
                ]),
            ).toStrictEqual([
                { children: [{ text: '' }], type: 'video-embed', resourceId: 'resourceId', service: 'youtube' },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'video-embed',
                        resourceId: 'resourceId',
                        service: 'youtube',
                        alt: 'alt',
                        extra: 1,
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [{ text: '' }],
                    type: 'video-embed',
                    resourceId: 'resourceId',
                    service: 'youtube',
                    alt: 'alt',
                },
            ]);
        });

        it('restores embed', () => {
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P' }] }, { type: 'embed' }]),
            ).toStrictEqual([{ children: [{ text: 'P' }], type: 'paragraph' }]);
            expect(normalizeEditorChildren([{ type: 'embed', source: 'source' }])).toStrictEqual([
                { children: [{ text: '' }], type: 'embed', source: 'source' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'embed', source: 'source', alt: 'alt', height: 100 }]),
            ).toStrictEqual([{ children: [{ text: '' }], type: 'embed', source: 'source', alt: 'alt', height: 100 }]);
        });

        it('restores block quote', () => {
            expect(
                normalizeEditorChildren([
                    { type: 'block-quote', children: [{ text: 'Q\nB' }, 0, { text: 'more', bold: true }] },
                ]),
            ).toStrictEqual([{ children: [{ text: 'Q\nB' }, { text: 'more', bold: true }], type: 'block-quote' }]);
        });

        it('restores code-block', () => {
            expect(
                normalizeEditorChildren([{ type: 'code-block', children: [{ text: 'code' }], language: 'language' }]),
            ).toStrictEqual([{ children: [{ text: 'code' }], type: 'code-block', language: 'language' }]);
            expect(
                normalizeEditorChildren([
                    { type: 'code-block', children: [{ text: 'code\nline' }], language: 'language', extra: 1 },
                ]),
            ).toStrictEqual([{ children: [{ text: 'code\nline' }], type: 'code-block', language: 'language' }]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'code-block',
                        children: [
                            { text: 'code\nline', bold: true },
                            { text: 'hello', italic: false, code: true, link: 'https://example.com' },
                        ],
                        language: 'language',
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [{ text: 'code\nline' }, { text: 'hello', link: 'https://example.com' }],
                    type: 'code-block',
                    language: 'language',
                },
            ]);
        });

        it('restores headings', () => {
            expect(
                normalizeEditorChildren([{ type: 'heading-one', children: [{ text: 'H' }], level: 1 }]),
            ).toStrictEqual([{ children: [{ text: 'H' }], type: 'heading-one' }]);
            expect(
                normalizeEditorChildren([{ type: 'heading-two', children: [{ text: 'H\nwith line' }] }]),
            ).toStrictEqual([{ children: [{ text: 'H with line' }], type: 'heading-two' }]);
            expect(normalizeEditorChildren([{ type: 'heading-three', children: ['H\nwith line'] }])).toStrictEqual([
                { children: [{ text: 'H with line' }], type: 'heading-three' },
            ]);
        });

        it('restores callout', () => {
            expect(
                normalizeEditorChildren([
                    { type: 'callout', children: [{ type: 'icon' }, { type: 'paragraph', children: 'C' }] },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'icon', icon: '💡', children: [{ text: '' }] },
                        { type: 'paragraph', children: [{ text: 'C' }] },
                    ],
                    type: 'callout',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    { type: 'callout', children: [{ type: 'icon' }, { type: 'paragraph', children: 'C' }] },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'icon', icon: '💡', children: [{ text: '' }] },
                        { type: 'paragraph', children: [{ text: 'C' }] },
                    ],
                    type: 'callout',
                },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'callout', children: [{ type: 'icon' }, { text: 'C' }] }]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'icon', icon: '💡', children: [{ text: '' }] },
                        { type: 'paragraph', children: [{ text: 'C' }] },
                    ],
                    type: 'callout',
                },
            ]);
            expect(normalizeEditorChildren([{ type: 'callout', children: [{ text: 'C' }] }])).toStrictEqual([
                {
                    children: [
                        { type: 'icon', icon: '💡', children: [{ text: '' }] },
                        { type: 'paragraph', children: [{ text: 'C' }] },
                    ],
                    type: 'callout',
                },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'callout', children: [{ text: 'C' }, { type: 'icon', icon: '🚧' }] }]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'icon', icon: '🚧', children: [{ text: '' }] },
                        { type: 'paragraph', children: [{ text: 'C' }] },
                    ],
                    type: 'callout',
                },
            ]);
        });

        it('restores lists', () => {
            expect(normalizeEditorChildren([{ type: 'numbered-list', children: [{ text: 'L' }] }])).toStrictEqual([
                { children: [{ type: 'list-item', children: [{ text: 'L' }] }], type: 'numbered-list' },
            ]);
            expect(normalizeEditorChildren([{ type: 'bulleted-list', children: [{ text: 'L' }] }])).toStrictEqual([
                { children: [{ type: 'list-item', children: [{ text: 'L' }] }], type: 'bulleted-list' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'numbered-list', children: [{ text: 'L' }, { text: 'L2' }] }]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        { type: 'list-item', children: [{ text: 'L2' }] },
                    ],
                    type: 'numbered-list',
                },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'bulleted-list', children: [{ text: 'L' }, { text: 'L2' }] }]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        { type: 'list-item', children: [{ text: 'L2' }] },
                    ],
                    type: 'bulleted-list',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'numbered-list',
                        children: [{ text: 'L' }, { type: 'table', children: [{ text: 'L2' }] }],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        { type: 'list-item', children: [{ text: 'L2' }] },
                    ],
                    type: 'numbered-list',
                },
            ]);
        });

        it('restores nested lists', () => {
            expect(
                normalizeEditorChildren([
                    {
                        type: 'numbered-list',
                        children: [{ text: 'L' }, { type: 'numbered-list', children: [{ text: 'L2' }] }],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        { type: 'numbered-list', children: [{ type: 'list-item', children: [{ text: 'L2' }] }] },
                    ],
                    type: 'numbered-list',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'bulleted-list',
                        children: [{ text: 'L' }, { type: 'bulleted-list', children: [{ text: 'L2' }] }],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        { type: 'bulleted-list', children: [{ type: 'list-item', children: [{ text: 'L2' }] }] },
                    ],
                    type: 'bulleted-list',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'numbered-list',
                        children: [
                            { text: 'L' },
                            0,
                            {
                                type: 'bulleted-list',
                                children: [
                                    { type: '', children: [{ text: 'L2' }] },
                                    { text: 'L2.1' },
                                    { type: 'numbered-list', children: ['L2.2'] },
                                ],
                                extra: 'extra',
                            },
                            'L3',
                        ],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }] },
                        {
                            type: 'bulleted-list',
                            children: [
                                { type: 'list-item', children: [{ text: 'L2' }] },
                                { type: 'list-item', children: [{ text: 'L2.1' }] },
                                {
                                    children: [{ type: 'list-item', children: [{ text: 'L2.2' }] }],
                                    type: 'numbered-list',
                                },
                            ],
                        },
                        { type: 'list-item', children: [{ text: 'L3' }] },
                    ],
                    type: 'numbered-list',
                },
            ]);
        });

        it('restores table', () => {
            expect(normalizeEditorChildren([{ type: 'table', children: [{ text: 'T' }] }])).toStrictEqual([
                { children: [{ text: 'T' }], type: 'paragraph' },
            ]);
            expect(normalizeEditorChildren([{ type: 'table-cell', children: [{ text: 'T' }] }])).toStrictEqual([
                {
                    children: [{ children: [{ children: [{ text: 'T' }], type: 'table-cell' }], type: 'table-row' }],
                    type: 'table',
                },
            ]);
            expect(normalizeEditorChildren([{ type: 'table-row', children: [{ text: 'T' }] }])).toStrictEqual([
                {
                    children: [{ children: [{ children: [{ text: 'T' }], type: 'table-cell' }], type: 'table-row' }],
                    type: 'table',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    { type: 'table', children: [{ type: 'table-cell', children: [{ text: 'T' }] }] },
                ]),
            ).toStrictEqual([
                {
                    children: [{ children: [{ children: [{ text: 'T' }], type: 'table-cell' }], type: 'table-row' }],
                    type: 'table',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        type: 'table',
                        children: [
                            { type: 'table-row', children: [{ text: 'T' }] },
                            { type: 'table-row', children: [{ text: 'T2' }, { type: 'table-cell', children: 'T2-2' }] },
                        ],
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [
                        {
                            children: [
                                { children: [{ text: 'T' }], type: 'table-cell' },
                                { children: [{ text: '' }], type: 'table-cell' },
                            ],
                            type: 'table-row',
                        },
                        {
                            children: [
                                { children: [{ text: 'T2' }], type: 'table-cell' },
                                { children: [{ text: 'T2-2' }], type: 'table-cell' },
                            ],
                            type: 'table-row',
                        },
                    ],
                    type: 'table',
                },
            ]);
            expect(
                normalizeEditorChildren([
                    {
                        children: [{ children: [{ children: [{ text: 'T' }], type: 'list-item' }], type: 'table-row' }],
                        type: 'table',
                    },
                ]),
            ).toStrictEqual([
                {
                    children: [{ children: [{ children: [{ text: 'T' }], type: 'table-cell' }], type: 'table-row' }],
                    type: 'table',
                },
            ]);
        });

        it('resolves formatting', () => {
            expect(normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P' }] }])).toStrictEqual([
                { children: [{ text: 'P' }], type: 'paragraph' },
            ]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', bold: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', bold: true }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', italic: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', italic: true }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', underline: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', underline: true }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', strikethrough: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', strikethrough: true }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', code: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', code: true }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', color: 'red' }] }]),
            ).toStrictEqual([{ children: [{ text: 'P' }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', underline: 12 }] }]),
            ).toStrictEqual([{ children: [{ text: 'P' }], type: 'paragraph' }]);
            expect(
                normalizeEditorChildren([{ type: 'paragraph', children: [{ text: 'P', bold: true, code: true }] }]),
            ).toStrictEqual([{ children: [{ text: 'P', bold: true, code: true }], type: 'paragraph' }]);
        });

        it('does not modify valid content', () => {
            const leafWithAllMarks = {
                text: 'T',
                bold: true,
                italic: true,
                underline: true,
                strikethrough: true,
                code: true,
                link: 'https://example.com',
                constant: 'constant',
            };
            const children = [
                { type: 'paragraph', children: [{ text: 'P\nline' }, leafWithAllMarks] },
                { type: 'heading-one', children: [{ text: 'H' }, leafWithAllMarks] },
                { type: 'heading-two', children: [{ text: 'H' }, leafWithAllMarks] },
                { type: 'heading-three', children: [leafWithAllMarks, { text: 'H' }] },
                {
                    type: 'code-block',
                    language: 'ts',
                    children: [{ text: 'C\nline' }, { text: 'hello', link: 'https://example.com' }],
                },
                { type: 'block-quote', children: [{ text: 'Q\nline' }, leafWithAllMarks] },
                {
                    type: 'callout',
                    children: [
                        { type: 'icon', icon: '🔥', children: [{ text: '' }] },
                        { type: 'paragraph', children: [leafWithAllMarks] },
                    ],
                },
                {
                    type: 'numbered-list',
                    start: 5,
                    children: [
                        { type: 'list-item', children: [{ text: 'L' }, leafWithAllMarks] },
                        {
                            type: 'bulleted-list',
                            children: [
                                { type: 'list-item', children: [{ text: 'L2' }, leafWithAllMarks] },
                                { type: 'list-item', children: [{ text: 'L3' }, leafWithAllMarks] },
                                {
                                    type: 'numbered-list',
                                    start: 2,
                                    children: [{ type: 'list-item', children: [leafWithAllMarks] }],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table',
                    columns: [{ width: 100 }, { width: 200 }],
                    children: [
                        {
                            type: 'table-row',
                            children: [
                                { type: 'table-cell', children: [leafWithAllMarks] },
                                { type: 'table-cell', children: [{ text: 'TC1' }] },
                            ],
                        },
                        {
                            type: 'table-row',
                            children: [
                                { type: 'table-cell', children: [leafWithAllMarks] },
                                { type: 'table-cell', children: [{ text: 'TC2' }] },
                            ],
                        },
                    ],
                },
                { type: 'horizontal-line', children: [{ text: '' }] },
                {
                    type: 'image',
                    children: [{ text: '' }],
                    src: 'https://example.com',
                    alt: 'alt',
                    link: 'https://example.com',
                    width: 200,
                    fingerprint: 'fingerprint',
                    originalWidth: 200,
                    originalHeight: 200,
                    fileName: 'fileName',
                    handle: 'handle',
                },
                {
                    type: 'video',
                    children: [{ text: '' }],
                    sources: [
                        { type: 'mp4', url: 'https://example.com' },
                        { type: 'mov', url: 'https://example.com' },
                    ],
                    alt: 'alt',
                    width: 200,
                    height: 200,
                },
                {
                    type: 'video-embed',
                    children: [{ text: '' }],
                    service: 'youtube',
                    resourceId: 'resourceId',
                    alt: 'alt',
                },
                {
                    type: 'embed',
                    children: [{ text: '' }],
                    source: 'https://example.com',
                    alt: 'alt',
                    height: 400,
                },
            ];
            const clonedChildren = JSON.parse(JSON.stringify(children));
            expect(normalizeEditorChildren(children)).toStrictEqual(clonedChildren);
        });

        it('general', () => {
            expect(
                normalizeEditorChildren([
                    { text: 'hello' },
                    { type: 'paragraphs', children: [{ text: 'TABLE OF CONTENTS', bold: true }] },
                ]),
            ).toStrictEqual([
                { children: [{ text: 'hello' }], type: 'paragraph' },
                { children: [{ text: 'TABLE OF CONTENTS', bold: true }], type: 'paragraph' },
            ]);
        });
    });
}

// #endregion
